/*
  All helper functions were provided by Reactflow's examples. 

  https://reactflow.dev/docs/examples/edges/floating-edges/
*/

import { Position } from 'reactflow';

/**
 * This helper function returns the intersection point
 * of the line between the center of the intersectionNode and the target node.
 */
function getNodeIntersection(intersectionNode, targetNode) {
  const {
    width: intersectionNodeWidth,
    height: intersectionNodeHeight,
    positionAbsolute: intersectionNodePosition,
  } = intersectionNode;

  const { width: targetNodeWidth, height: targetNodeHeight, positionAbsolute: targetNodePosition } = targetNode;

  const w = intersectionNodeWidth / 2;
  const h = intersectionNodeHeight / 2;

  const targetW = targetNodeWidth / 2;
  const targetH = targetNodeHeight / 2;

  const x2 = intersectionNodePosition.x + w;
  const y2 = intersectionNodePosition.y + h;
  const x1 = targetNodePosition.x + targetW;
  const y1 = targetNodePosition.y + targetH;

  let xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  xx1 = xx1 ? xx1 : 1;
  let yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  yy1 = yy1 ? yy1 : 1;
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1));
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

/**
 * Returns the position (top,right,bottom or right) passed node compared to the intersection point
 */
function getEdgePosition(node, intersectionPoint) {
  const n = { ...node.positionAbsolute, ...node };
  const nx = Math.round(n.x);
  const ny = Math.round(n.y);
  const px = Math.round(intersectionPoint.x);
  const py = Math.round(intersectionPoint.y);

  if (px <= nx + 1) {
    return Position.Left;
  }
  if (px >= nx + n.width - 1) {
    return Position.Right;
  }
  if (py <= ny + 1) {
    return Position.Top;
  }
  if (py >= n.y + n.height - 1) {
    return Position.Bottom;
  }

  return Position.Top;
}

/**
 * Returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
 */
export function getEdgeParams(source, target) {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
  const targetPos = getEdgePosition(target, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos,
  };
}
